import { Controller } from "@hotwired/stimulus"
import paper from "paper"

/**
 * Block Flow Preview - Paper.js visual preview of Slack message blocks
 * 
 * Shows blocks as vertically stacked cards connected by wires.
 * Matches the style of outlet_flow_editor_controller.js but with
 * a vertical layout and 10px gaps between blocks.
 */
export default class extends Controller {
  static targets = ["canvas"]

  static values = {
    blocks: Array,
    templateName: String,
    gridSize: { type: Number, default: 20 },
    nodeWidth: { type: Number, default: 280 },
    nodeHeight: { type: Number, default: 56 },
    nodeGap: { type: Number, default: 30 }
  }

  connect() {
    console.log('[BlockFlowPreview] Controller connected')
    console.log('[BlockFlowPreview] blocks:', this.blocksValue)
    console.log('[BlockFlowPreview] canvas:', this.canvasTarget)
    console.log('[BlockFlowPreview] canvas size:', this.canvasTarget?.offsetWidth, 'x', this.canvasTarget?.offsetHeight)
    
    this.setupPaper()
    this.drawGrid()
    this.renderBlocks()
  }

  disconnect() {
    if (this.paperScope) {
      this.paperScope.remove()
    }
  }

  // Re-render when blocks value changes
  blocksValueChanged() {
    console.log('[BlockFlowPreview] blocksValueChanged, new blocks:', this.blocksValue)
    if (this.paperScope && this.nodesLayer) {
      this.renderBlocks()
    }
  }

  setupPaper() {
    this.paperScope = new paper.PaperScope()
    this.paperScope.setup(this.canvasTarget)

    this.project = this.paperScope.project
    this.view = this.paperScope.view

    // Layer organization
    this.gridLayer = new this.paperScope.Layer({ name: 'grid' })
    this.cablesLayer = new this.paperScope.Layer({ name: 'cables' })
    this.nodesLayer = new this.paperScope.Layer({ name: 'nodes' })
  }

  drawGrid() {
    this.gridLayer.activate()
    this.gridLayer.removeChildren()

    const bounds = this.view.bounds
    const gridSize = this.gridSizeValue

    // Draw dot grid (like n8n / outlet flow editor)
    for (let x = 0; x < bounds.width; x += gridSize) {
      for (let y = 0; y < bounds.height; y += gridSize) {
        new this.paperScope.Path.Circle({
          center: [x, y],
          radius: 1,
          fillColor: '#e5e7eb'
        })
      }
    }

    this.nodesLayer.activate()
  }

  renderBlocks() {
    console.log('[BlockFlowPreview] renderBlocks called')
    console.log('[BlockFlowPreview] blocksValue:', this.blocksValue)
    
    this.nodesLayer.activate()
    this.nodesLayer.removeChildren()
    this.cablesLayer.activate()
    this.cablesLayer.removeChildren()

    const blocks = this.blocksValue || []
    if (blocks.length === 0) {
      this.renderEmptyState()
      return
    }

    // Calculate starting position (centered horizontally)
    const canvasWidth = this.canvasTarget.offsetWidth
    const startX = Math.max(20, (canvasWidth - this.nodeWidthValue) / 2)
    const startY = 20

    // Track node positions for cables
    const nodePositions = []

    // Render each block as a card
    blocks.forEach((block, index) => {
      const y = startY + (index * (this.nodeHeightValue + this.nodeGapValue))
      const position = new this.paperScope.Point(startX, y)
      
      this.createBlockNode(block, position, index)
      
      // Store connector positions for cables
      nodePositions.push({
        bottom: new this.paperScope.Point(
          startX + this.nodeWidthValue / 2,
          y + this.nodeHeightValue
        ),
        top: new this.paperScope.Point(
          startX + this.nodeWidthValue / 2,
          y
        )
      })
    })

    // Draw connecting cables between blocks
    this.cablesLayer.activate()
    for (let i = 0; i < nodePositions.length - 1; i++) {
      this.createVerticalCable(
        nodePositions[i].bottom,
        nodePositions[i + 1].top
      )
    }

    this.nodesLayer.activate()
  }

  createBlockNode(block, position, index) {
    this.nodesLayer.activate()
    
    const group = new this.paperScope.Group()
    group.data = { type: 'block', block, index }

    // Determine block style based on type
    const style = this.getBlockStyle(block.type)

    // Card background with shadow
    const card = new this.paperScope.Path.Rectangle({
      point: position,
      size: [this.nodeWidthValue, this.nodeHeightValue],
      radius: 8,
      fillColor: 'white',
      strokeColor: '#e5e7eb',
      strokeWidth: 1,
      shadowColor: new this.paperScope.Color(0, 0, 0, 0.08),
      shadowBlur: 8,
      shadowOffset: new this.paperScope.Point(0, 2)
    })
    group.addChild(card)

    // Left color accent bar
    const accentBar = new this.paperScope.Path.Rectangle({
      point: [position.x, position.y],
      size: [4, this.nodeHeightValue],
      radius: [[4, 0], [0, 0], [0, 0], [4, 0]],
      fillColor: style.color
    })
    group.addChild(accentBar)

    // Block type icon background
    const iconBg = new this.paperScope.Path.Circle({
      center: [position.x + 28, position.y + this.nodeHeightValue / 2],
      radius: 14,
      fillColor: style.bgColor
    })
    group.addChild(iconBg)

    // Block type icon (emoji)
    const icon = new this.paperScope.PointText({
      point: [position.x + 21, position.y + this.nodeHeightValue / 2 + 5],
      content: style.icon,
      fontSize: 14
    })
    group.addChild(icon)

    // Block type label
    const typeLabel = new this.paperScope.PointText({
      point: [position.x + 52, position.y + 24],
      content: style.label,
      fontSize: 12,
      fontWeight: 'bold',
      fillColor: '#1f2937'
    })
    group.addChild(typeLabel)

    // Block content preview (truncated)
    const contentPreview = this.getBlockContentPreview(block)
    const content = new this.paperScope.PointText({
      point: [position.x + 52, position.y + 40],
      content: this.truncateText(contentPreview, 32),
      fontSize: 11,
      fillColor: '#6b7280'
    })
    group.addChild(content)

    // Top connector (except for first block)
    if (index > 0) {
      const topConnector = new this.paperScope.Path.Circle({
        center: [position.x + this.nodeWidthValue / 2, position.y],
        radius: 5,
        fillColor: '#10b981',
        strokeColor: 'white',
        strokeWidth: 2
      })
      group.addChild(topConnector)
    }

    // Bottom connector (except for last block)
    const blocks = this.blocksValue || []
    if (index < blocks.length - 1) {
      const bottomConnector = new this.paperScope.Path.Circle({
        center: [position.x + this.nodeWidthValue / 2, position.y + this.nodeHeightValue],
        radius: 5,
        fillColor: '#10b981',
        strokeColor: 'white',
        strokeWidth: 2
      })
      group.addChild(bottomConnector)
    }

    return group
  }

  createVerticalCable(from, to) {
    this.cablesLayer.activate()

    // Simple vertical line with slight curve
    const path = new this.paperScope.Path({
      strokeColor: '#10b981',
      strokeWidth: 2,
      strokeCap: 'round'
    })

    // Small gap from connectors
    const startY = from.y + 5
    const endY = to.y - 5

    path.add(new this.paperScope.Point(from.x, startY))
    path.add(new this.paperScope.Point(to.x, endY))

    this.nodesLayer.activate()
    return path
  }

  renderEmptyState() {
    this.nodesLayer.activate()

    const canvasWidth = this.canvasTarget.offsetWidth
    const canvasHeight = this.canvasTarget.offsetHeight

    // Empty state text
    const text = new this.paperScope.PointText({
      point: [canvasWidth / 2, canvasHeight / 2],
      content: 'No blocks configured',
      fontSize: 12,
      fillColor: '#9ca3af',
      justification: 'center'
    })
  }

  getBlockStyle(type) {
    const styles = {
      header: { 
        color: '#4A154B', 
        bgColor: '#f3e8ff',
        icon: 'H',
        label: 'Header'
      },
      section: { 
        color: '#3b82f6', 
        bgColor: '#dbeafe',
        icon: '§',
        label: 'Section'
      },
      divider: { 
        color: '#6b7280', 
        bgColor: '#f3f4f6',
        icon: '—',
        label: 'Divider'
      },
      context: { 
        color: '#8b5cf6', 
        bgColor: '#ede9fe',
        icon: 'i',
        label: 'Context'
      },
      image: { 
        color: '#10b981', 
        bgColor: '#d1fae5',
        icon: '🖼',
        label: 'Image'
      },
      actions: { 
        color: '#f59e0b', 
        bgColor: '#fef3c7',
        icon: '⚡',
        label: 'Actions'
      }
    }
    return styles[type] || { 
      color: '#6b7280', 
      bgColor: '#f3f4f6',
      icon: '?',
      label: type || 'Block'
    }
  }

  getBlockContentPreview(block) {
    if (!block) return ''

    switch (block.type) {
      case 'header':
        return block.text?.text || 'Header text...'
      case 'section':
        if (block.text?.text) {
          return block.text.text
        }
        if (block.fields?.length) {
          return `${block.fields.length} field(s)`
        }
        return 'Section content...'
      case 'divider':
        return '─────────'
      case 'context':
        if (block.elements?.length) {
          const firstElement = block.elements[0]
          if (firstElement?.text) {
            return firstElement.text
          }
          return `${block.elements.length} element(s)`
        }
        return 'Context info...'
      case 'image':
        return block.alt_text || 'Image'
      case 'actions':
        if (block.elements?.length) {
          return `${block.elements.length} button(s)`
        }
        return 'Interactive elements'
      default:
        return JSON.stringify(block).slice(0, 30) + '...'
    }
  }

  truncateText(text, maxLength) {
    if (!text) return ''
    // Remove template variables for cleaner preview
    const cleaned = text.replace(/\{\{[^}]+\}\}/g, '•••')
    return cleaned.length > maxLength 
      ? cleaned.slice(0, maxLength) + '...' 
      : cleaned
  }

  // Handle resize
  handleResize() {
    if (this.view) {
      this.view.viewSize = new this.paperScope.Size(
        this.canvasTarget.offsetWidth,
        this.canvasTarget.offsetHeight
      )
      this.drawGrid()
      this.renderBlocks()
    }
  }
}
